import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from 'lucide-react';
type Page = {
  image: string;
  label: string;
};
const pages: Page[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/h3GYWWu3zbqQKAxGMsFMYB/Captura_de_pantalla_2026-07-27_a_las_3.29.12_p.webp',
  label: 'Session card'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/saBW52bLz6me5n7KnRWEMX/Captura_de_pantalla_2026-07-27_a_las_3.30.24_p.webp',
  label: 'Drill breakdown'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/pvMBygkVYZ2kM5BoyJRvkh/Captura_de_pantalla_2026-07-27_a_las_3.31.33_p.webp',
  label: 'Pitch diagrams'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/cnHd85AorJKPY2BvB96JHo/Captura_de_pantalla_2026-07-27_a_las_3.33.35_p.webp',
  label: 'Technique blueprint'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/hjoLDv7y2BKaRCARVUqrUr/Captura_de_pantalla_2026-07-27_a_las_3.34.38_p.webp',
  label: 'Conditioning games'
}];
export function InsideLookSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = (nextIndex: number, nextDirection: number) => {
    setDirection(nextDirection);
    setIndex((nextIndex + pages.length) % pages.length);
  };
  const current = pages[index];
  return <section className="bg-[#FAFAFA] px-5 py-20" data-id="element-119" data-mp="24l8o1ucqm0fw">
      <div className="mx-auto max-w-6xl" data-id="element-120" data-mp="24icoy2p5l5pv">
        <div className="text-center" data-id="element-121" data-mp="29iioe51nzwii">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[.2em] text-[#b18b18]" data-id="element-122" data-mp="29jjx42v7k6x2">
            <EyeIcon className="h-4 w-4" data-id="element-123" data-mp="2egk2sco5b7nn" />
            See it before you buy
          </p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase leading-none text-[#0A0A0A] sm:text-5xl" data-id="element-124" data-mp="2jb6iut569oe2">
            Take a look <span className="text-[#b18b18]" data-id="element-125" data-mp="1qsj0vmlh2ph7">inside</span>
          </h2>
        </div>

        <div className="mt-9 border border-[#E5E5EA] bg-white p-3 shadow-xl sm:p-8" data-id="element-126" data-mp="1is3a7aleqmvr">
          <div className="relative" data-id="element-127" data-mp="1iq08vuet17bq">
            <AnimatePresence initial={false} mode="wait" custom={direction} data-id="element-128" data-mp="1er19epbue2ro">
              <motion.figure key={current.image} custom={direction} initial={{
              opacity: 0,
              x: direction * 48
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -48
            }} transition={{
              duration: 0.28,
              ease: 'easeOut'
            }} className="mx-auto w-full max-w-2xl" data-id="element-129" data-mp="1shwjg4y2upwu">
                <div className="overflow-hidden border-2 border-[#0A0A0A] bg-[#0A0A0A] shadow-2xl" data-id="element-130" data-mp="19rew6fw0kmiv">
                  <img src={current.image} alt={`${current.label} — page preview`} loading="lazy" className="h-auto w-full object-contain" data-id="element-131" data-mp="14sbrwxnuykby" />
                </div>
                <figcaption className="mt-4 text-center text-[10px] font-bold uppercase tracking-[.2em] text-[#5A5A66]" data-id="element-132" data-mp="2g6z3tkk3iwv1">
                  {current.label}
                </figcaption>
              </motion.figure>
            </AnimatePresence>

            <button type="button" onClick={() => goTo(index - 1, -1)} aria-label="Previous page" className="absolute left-0 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border-2 border-[#d4af37] bg-white text-[#0A0A0A] shadow-lg transition-colors hover:bg-[#d4af37] sm:h-14 sm:w-14" data-id="element-133" data-mp="3p4p173ptblpc">
              <ChevronLeftIcon className="h-7 w-7" data-id="element-134" data-mp="pr4u1bf0jxk3" />
            </button>
            <button type="button" onClick={() => goTo(index + 1, 1)} aria-label="Next page" className="absolute right-0 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border-2 border-[#d4af37] bg-white text-[#0A0A0A] shadow-lg transition-colors hover:bg-[#d4af37] sm:h-14 sm:w-14" data-id="element-135" data-mp="bxdtutn69q11">
              <ChevronRightIcon className="h-7 w-7" data-id="element-136" data-mp="3gxxbyq882plc" />
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3" data-id="element-137" data-mp="3bxi0633l9q5h">
            {pages.map((page, pageIndex) => <button key={page.image} type="button" aria-label={`Go to ${page.label}`} aria-current={pageIndex === index} onClick={() => goTo(pageIndex, pageIndex > index ? 1 : -1)} className={`h-2.5 rounded-full transition-all ${pageIndex === index ? 'w-8 bg-[#b18b18]' : 'w-2.5 bg-[#0A0A0A]/20 hover:bg-[#0A0A0A]/40'}`} data-id="element-138" data-mp="37wpt9gi6vxcd" data-mpi={pageIndex} />)}
          </div>
        </div>

        <div className="mt-9 text-center" data-id="element-139" data-mp="n5p1oe62btgf">
          <a href="#offer-details" className="cta-button inline-flex w-full items-center justify-center rounded-2xl px-6 py-6 text-xl font-bold sm:w-auto sm:text-2xl" data-id="element-140" data-mp="n7i8btgoedof">
            GET RUGBY READY NOW
          </a>
        </div>
      </div>
    </section>;
}